import { useState } from "react";
import { api, NH_SIGN_IN_URL, PUBLIC_API_URL } from "../api";
import { Icon, IconName } from "../ui";
import { CURL } from "./Dashboard";
import { INDICES_LINE, PlanCards, usePlans } from "./Pricing";

type State = "form" | "sending" | "sent";

interface Feature {
  icon: IconName;
  title: string;
  body: React.ReactNode;
}

const PROOF = ["Free to start", "No password, sign in by email", "Included for NilssonHedge subscribers"];

const CORE: Feature[] = [
  { icon: "code", title: "Fund data API", body: "Search funds and pull their monthly track records from Python, R or any HTTP client." },
  { icon: "chart", title: "NilssonHedge indices", body: "Strategy benchmarks for hedge funds and CTAs, on every plan, Free included." },
  { icon: "download", title: "Watchlists and CSV export", body: "Save the funds you follow and download their full histories in one click." },
];

// Institutional (tier `pro`) gets the premium MCP; Subscriber Plus has the basic one (search and track records).
const PRO: Feature[] = [
  { icon: "sparkles", title: "Premium AI assistant (MCP)", body: "Ask Claude to compare funds, screen the database and check drawdowns, in plain English. Answers come with the numbers behind them." },
  { icon: "grid", title: "Excel add-in", body: <><code className="font-mono text-main">=NH.RETURNS()</code> drops a fund's full track record into your sheet.</> },
  { icon: "book", title: "Factsheets, tools and alerts", body: "One-page fund factsheets, screening tools and alerts on the funds you follow." },
  { icon: "bars", title: "Monthly research", body: "A research note each month on what the NilssonHedge database shows across strategies and funds." },
];

const STEPS = ["Sign in with your email", "Create an API token", "Make your first call"];

function FeatureItem({ f }: { f: Feature }) {
  return (
    <li className="flex flex-col gap-3">
      <div className="flex items-center justify-center w-10 h-10 rounded-lg border border-border bg-card text-accent">
        <Icon name={f.icon} />
      </div>
      <div className="text-sm leading-relaxed">
        <p className="text-[15px] font-semibold text-main">{f.title}</p>
        <p className="mt-1 text-muted">{f.body}</p>
      </div>
    </li>
  );
}

export function Login() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<State>("form");
  const [error, setError] = useState<string | null>(null);
  const { plans } = usePlans();

  const params = new URLSearchParams(window.location.search);
  const redirectError = params.get("error");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;

    setState("sending");
    setError(null);
    try {
      await api.post("/auth/magic-link", { email: email.trim() });
      setState("sent");
    } catch {
      setState("form");
      setError("Something went wrong. Please try again.");
    }
  }

  return (
    <div className="flex flex-col gap-24 max-sm:gap-16 py-10 max-sm:py-2">
      <section className="grid lg:grid-cols-[1fr_420px] gap-16 max-lg:gap-10 items-center">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-accent">The NilssonHedge database, by API</p>
          <h1 className="mt-3 text-5xl max-sm:text-[34px] leading-[1.1] font-bold tracking-tight text-balance text-main">
            Hedge fund data, wherever you work.
          </h1>
          <p className="mt-5 max-w-[54ch] text-lg max-sm:text-base text-muted">
            Track records and fund details for 7,000+ hedge funds and CTAs, plus the NilssonHedge indices. Pull them
            into your models, your spreadsheets and your AI assistant.
          </p>
          <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-main">
            {PROOF.map((p) => (
              <li key={p} className="flex items-center gap-2">
                <span aria-hidden className="text-accent font-bold">✓</span>
                {p}
              </li>
            ))}
          </ul>
        </div>

        <div id="sign-in" className="scroll-mt-8 p-8 max-sm:p-6 rounded-lg bg-card border border-border">
          <h2 className="text-xl font-semibold text-main">Sign in or create an account</h2>
          <p className="mt-1 mb-6 text-sm text-muted">We'll email you a sign-in link. No password needed.</p>

          {redirectError === "invalid_link" && (
            <p className="text-neg text-sm mb-4">This sign-in link has expired or was already used. Enter your email to get a new one.</p>
          )}

          {state === "sent" ? (
            <div className="bg-accent/10 text-accent rounded-lg p-5">
              <p className="font-semibold mb-1">Check your email</p>
              <p className="text-sm">
                We sent a login link to <strong>{email}</strong>. Click it to sign in. It expires in 15 minutes.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <input
                type="email"
                required
                placeholder="you@example.com"
                aria-label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-md bg-input border border-input-border text-main text-[15px] focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                disabled={state === "sending"}
              />
              {error && <p className="text-neg text-sm">{error}</p>}
              <button
                type="submit"
                disabled={state === "sending"}
                className="w-full px-8 py-3 rounded-md text-[15px] font-medium text-white bg-accent hover:bg-accent-hover cursor-pointer transition-colors disabled:opacity-50"
              >
                {state === "sending" ? "Sending..." : "Continue with email"}
              </button>
              <p className="text-xs text-muted">
                By continuing you confirm you are a qualified investor and agree to the{" "}
                <a href="/terms">Terms of Service</a>.
              </p>
            </form>
          )}

          <div className="mt-6 pt-6 border-t border-border flex flex-col gap-2">
            <a href={NH_SIGN_IN_URL} className="btn-default text-main justify-center py-3">Continue with NilssonHedge</a>
            <p className="text-xs text-muted">NilssonHedge subscribers are signed in with their plan.</p>
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-2xl font-bold tracking-tight text-main">Start with the data</h2>
        <p className="mt-2 max-w-[60ch] text-muted">Every account gets the API, the indices and a dashboard to manage tokens and usage.</p>
        <ul className="mt-8 grid sm:grid-cols-3 gap-8">
          {CORE.map((f) => <FeatureItem key={f.title} f={f} />)}
        </ul>
      </section>

      {plans && (
        <section>
          <h2 className="text-2xl font-bold tracking-tight text-main">Pick your plan</h2>
          <p className="mt-2 mb-8 max-w-[60ch] text-muted">{INDICES_LINE}</p>
          <PlanCards plans={plans} user={null} />
          <a href="/pricing" className="inline-block mt-6 text-[15px] font-medium text-accent hover:text-accent-hover">
            Compare plans side by side →
          </a>
        </section>
      )}

      <section className="p-10 max-sm:p-6 rounded-xl border border-accent/30 bg-accent/5">
        <p className="text-sm font-semibold text-accent">Institutional</p>
        <h2 className="mt-2 text-2xl font-bold tracking-tight text-main">Your research desk, built on NilssonHedge</h2>
        <p className="mt-2 max-w-[60ch] text-muted">
          Institutional puts the database where the work happens: in your AI assistant, in Excel and on a fund's factsheet.
        </p>
        <ul className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {PRO.map((f) => <FeatureItem key={f.title} f={f} />)}
        </ul>
        <div className="mt-10 flex flex-wrap gap-3">
          <a href="/pricing" className="btn-primary text-white">Compare plans</a>
          <a href="/contact?interest=pro" className="btn-default text-main">Talk to us</a>
        </div>
      </section>

      <section className="grid lg:grid-cols-[1fr_1.4fr] gap-12 max-lg:gap-8 items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-main">Your first call in a minute</h2>
          <ol className="mt-6 flex flex-col gap-4">
            {STEPS.map((s, i) => (
              <li key={s} className="flex items-center gap-3 text-[15px] text-main">
                <span className="flex items-center justify-center w-7 h-7 shrink-0 rounded-full bg-accent/10 text-accent text-sm font-semibold">{i + 1}</span>
                {s}
              </li>
            ))}
          </ol>
          <a href={`${PUBLIC_API_URL}/docs`} target="_blank" rel="noreferrer" className="inline-block mt-6 text-[15px] font-medium text-accent hover:text-accent-hover">
            Read the API docs →
          </a>
        </div>
        <pre className="min-w-0 p-5 rounded-lg bg-stat border border-border text-[13px] leading-relaxed font-mono text-main whitespace-pre-wrap break-all">{CURL}</pre>
      </section>

      <section className="flex max-md:flex-col md:items-center justify-between gap-6 p-10 max-sm:p-6 rounded-xl bg-stat border border-border">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-main">Start free today</h2>
          <p className="mt-2 max-w-[60ch] text-muted">
            Already a NilssonHedge subscriber? The API is included for up to 75 funds a quarter, and{" "}
            <a href="/pricing">Subscriber Plus</a> adds the full database and an AI assistant.
          </p>
        </div>
        <a href="#sign-in" className="btn-primary text-white self-start md:self-auto">Get started free</a>
      </section>
    </div>
  );
}
